import { getDateAsUtc } from './getStringDate';
import { fromDatetimeLocalValue } from './tournamentDateTime';
import {
  IMatch,
  PlayValues,
} from '../pages/tourDetails/TournamentPlay/components/PlayTag/PlayTag';
import { toBoolean } from './common.helpers';
import { TournamentCategory } from '../types/tournament';

const BRACKET_CELL_FLAT_KEYS = [
  'bracketCellId',
  'bracketCellMatchIndex',
  'bracketCellMatchCount',
  'bracketCellStatus',
  'bracketCellProgressionMode',
  'bracketCellWinnerTeamId',
  'bracketCellLoserTeamId',
  'bracketCellMatchSuperseded',
] as const;

export function stripBracketCellFlatFields<T extends Record<string, unknown>>(
  row: T,
): T {
  const o = { ...row };
  for (const k of BRACKET_CELL_FLAT_KEYS) {
    delete o[k];
  }
  return o;
}

function courtIdFromValue(court: unknown): number | null {
  if (court == null) {
    return null;
  }
  if (typeof court === 'number') {
    return court;
  }
  if (typeof court === 'object' && court !== null && 'id' in court) {
    const id = (court as { id: unknown }).id;
    return typeof id === 'number' ? id : null;
  }
  return null;
}

function teamRef(team: unknown): { id: number } | null | undefined {
  if (team == null) {
    return null;
  }
  if (typeof team === 'object' && team !== null && 'id' in team) {
    const id = (team as { id: unknown }).id;
    if (typeof id === 'number') {
      return { id };
    }
  }
  return undefined;
}

function playerRef(player: unknown): { id: number } | null | undefined {
  if (player == null) {
    return null;
  }
  if (typeof player === 'object' && player !== null && 'id' in player) {
    const id = (player as { id: unknown }).id;
    if (typeof id === 'number') {
      return { id };
    }
  }
  return undefined;
}

function categoryRef(
  category: unknown,
  categoryId: unknown,
): { id: number } | undefined {
  if (typeof category === 'object' && category !== null && 'id' in category) {
    const id = (category as { id: unknown }).id;
    if (typeof id === 'number') {
      return { id };
    }
  }
  if (typeof categoryId === 'number') {
    return { id: categoryId };
  }
  return undefined;
}

function groupRef(group: unknown, groupId: unknown): { id: number } | undefined {
  if (typeof group === 'object' && group !== null && 'id' in group) {
    const id = (group as { id: unknown }).id;
    if (typeof id === 'number') {
      return { id };
    }
  }
  if (typeof groupId === 'number') {
    return { id: groupId };
  }
  return undefined;
}

function startDatePayload(
  startDate: string | undefined | null,
  timeZone?: string,
): string | null {
  if (!startDate) {
    return null;
  }
  return timeZone ? fromDatetimeLocalValue(startDate, timeZone) : getDateAsUtc(startDate);
}

export function mapMatchToBracketCellPatchPayload(
  match: IMatch,
  timeZone?: string,
): Record<string, unknown> {
  const payload = mapMatchToClassPlayPayload(match, timeZone);
  if (typeof match.bracketCellMatchIndex === 'number') {
    payload.bracketCellMatchIndex = match.bracketCellMatchIndex;
  }
  return payload;
}

export function mapMatchToClassPlayPayload(
  match: IMatch,
  timeZone?: string,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    bracket: Number(match.bracket),
    isGold: toBoolean(match.isGold),
    court: match.court ? { id: courtIdFromValue(match.court) } : null,
  };

  if (typeof match.id === 'number') {
    payload.id = match.id;
  }
  if (match.name !== undefined) {
    payload.name = match.name;
  }
  if (match.matchId && match.matchId !== '') {
    payload.matchId = match.matchId;
  }
  if (match.type !== undefined) {
    payload.type = match.type;
  }
  if (match.sortKey != null) {
    payload.sortKey = match.sortKey;
  }
  if (match.homeTeamPlaceholder != null) {
    payload.homeTeamPlaceholder = match.homeTeamPlaceholder;
  }
  if (match.awayTeamPlaceholder != null) {
    payload.awayTeamPlaceholder = match.awayTeamPlaceholder;
  }

  const resolvedDate = startDatePayload(match.startDate, timeZone);
  payload.startDate = resolvedDate;
  payload.scheduleDate = resolvedDate;
  payload.scheduleEndDate = null;

  const homeTeam = teamRef(match.homeTeam);
  if (homeTeam !== undefined) {
    payload.homeTeam = homeTeam;
  }

  const awayTeam = teamRef(match.awayTeam);
  if (awayTeam !== undefined) {
    payload.awayTeam = awayTeam;
  }

  for (const key of ['homePlayer1', 'homePlayer2', 'awayPlayer1', 'awayPlayer2'] as const) {
    const ref = playerRef(match[key]);
    if (ref !== undefined) {
      payload[key] = ref;
    }
  }

  const tournamentCategory = categoryRef(
    match.tournamentCategory,
    match.tournamentCategoryId,
  );
  if (tournamentCategory) {
    payload.tournamentCategory = tournamentCategory;
  }

  const tournamentGroup = groupRef(match.tournamentGroup, match.tournamentGroupId);
  if (tournamentGroup) {
    payload.tournamentGroup = tournamentGroup;
  }

  if (match.isTiebreaker) {
    payload.isTiebreaker = true;
  }
  if (match.tiebreakerPending) {
    payload.tiebreakerPending = true;
  }

  return payload;
}

export const playTagTypeConverter = (
  playValue: PlayValues,
  _fullClass?: {
    format?: number;
    moveBox?: boolean;
    tournamentGroups?: PlayValues['tournamentGroups'];
  },
  timeZone?: string,
) => {
  return {
    tournamentGroups: playValue.tournamentGroups.map((group) => {
      if (typeof group.id === 'string') {
        const { id, ...rest } = group;

        return rest;
      } else {
        return group;
      }
    }),
    tournamentCategories: playValue.tournamentCategories
      ? playValue.tournamentCategories.map((category) => {
          let categoryData = category;
          if (typeof category.id === 'string') {
            const { id, ...rest } = category;
            categoryData = rest as TournamentCategory;
          }

          return {
            ...categoryData,
            isActive: toBoolean(categoryData.isActive),
          };
        })
      : [],
    tournamentMatches: Object.values(playValue.tournamentMatches)
      .filter(({ id }) => Boolean(id))
      .filter(
        (match) =>
          !(
            typeof match.id === 'string' &&
            typeof match.bracketCellId === 'string' &&
            match.bracketCellId
          ),
      )
      .map((match) => mapMatchToClassPlayPayload(match, timeZone)),
    ...(playValue.pendingDeletedTopLevelMatchIds?.length
      ? { deletedMatchIds: playValue.pendingDeletedTopLevelMatchIds }
      : {}),
  };
};
